import Axios, { AxiosError } from 'axios';
import restApi from '../../api/restApi';
import { AuthenticationStatus, OktaSessionState, QwaSession, QwaUser } from '../../auth/types';
import { isServerSide } from '../rendering';
import axios from 'axios';
import Router from 'next/router';
import { JsonProblem } from '../../middleware/error-handling-middleware';

import sessionApi from './api/session';
import { APP_STORE } from '../store/redux';
import { getSubscriberUserInfo } from './customHooks';
import { SUBS_SCHEMA } from '../../config/auth';

export type SignInOptions = {
  username: string;
  password: string;
  redirect?: boolean;
  authJwt?: string;
};

export type SignInResult = {
  /**
   * Will be different error codes,
   * depending on the type of error.
   */
  error: string | undefined;
  /**
   * HTTP status code,
   * hints the kind of error that happened.
   */
  status: number | undefined;
  /**
   * `true` if the signin was successful
   */
  ok: boolean;
  /**
   * `null` if there was an error,
   * otherwise the url the user
   * should have been redirected to.
   */
  url: string | undefined;
};

/**
 * Intended for client side.
 *
 * Will redirect to Okta redirect URL, and then to `/login_successful`, by using NextJS's router
 */
export const signIn = async ({
  username,
  password,
  redirect = true,
  authJwt,
}: SignInOptions): Promise<SignInResult> => {
  if (!username) {
    return {
      error: `Username is required`,
      status: undefined,
      ok: false,
      url: undefined,
    };
  }

  if (!password) {
    return {
      error: `Password is required`,
      status: undefined,
      ok: false,
      url: undefined,
    };
  }

  let callbackUrl: string | undefined = undefined;
  try {
    const loginData = {
      username,
      password,
      authJwt,
    };
    const store = APP_STORE;
    // TODO ADD Auth0 sign in without prompt.
    // const createSessionResult = await store.dispatch(sessionApi.endpoints.createSession.initiate(loginData));
    return {
      error: 'cannot login with okta',
      status: 500,
      ok: false,
      url: undefined,
    };
    // if ('error' in createSessionResult) {
    //   // rethrow the caught error in redux to reuse existing error handling logic
    //   throw createSessionResult.error;
    // }

    // const { data } = createSessionResult;

    // callbackUrl = data.sessionRedirect;

    // return {
    //   error: undefined,
    //   status: 200,
    //   ok: true,
    //   url: data.sessionRedirect,
    // };
    // return response.data;
  } catch (error) {
    // TODO log error via rollbar?

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<JsonProblem>;
      const problem = axiosError.response?.data;
      return {
        error: problem?.detail || axiosError.message,
        status: problem?.status || axiosError.status || 500,
        ok: false,
        url: undefined,
      };
    }
  } finally {
    if (!isServerSide()) {
      //  removeItem() does not throw on a non-existent item
      localStorage.removeItem('contentrecommendation-listpage-filters');
      localStorage.removeItem('content-recommendations-urls');
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(`contentrecommendation-listpage-filters`);
    }
    if (callbackUrl && redirect && !isServerSide()) {
      Router.push(callbackUrl);
    }
  }

  // default error
  return {
    error: `Unknown error occurred`,
    status: undefined,
    ok: false,
    url: callbackUrl,
  };
};

export type SignOutOptions = {
  redirect?: boolean;
  callbackUrl?: string;
};

export type SignOutResult = {
  /**
   * Will be different error codes,
   * depending on the type of error.
   */
  error: string | undefined;
  /**
   * HTTP status code,
   * hints the kind of error that happened.
   */
  status: number | undefined;
  /**
   * `true` if the signin was successful
   */
  ok: boolean;
  /**
   * `null` if there was an error,
   * otherwise the url the user
   * should have been redirected to.
   */
  url: string | undefined;
};

/**
 * Intended for client side.
 *
 * Will redirect to homepage by default using NextJS's router if used on client side
 */
export const signOut = async ({ callbackUrl = '/', redirect = true }: SignOutOptions = {}): Promise<SignOutResult> => {
  try {
    if (!isServerSide()) {
      localStorage.setItem('logged_out', 'true');

      //  removeItem() does not throw on a non-existent item
      localStorage.removeItem('userInfo');
      localStorage.removeItem('sub-userInfo');
      // this localStorage key is no longer used, but should keep this to clear it out
      localStorage.removeItem('User_Selected_Schema');
      localStorage.removeItem('subscriber-auth-token');
      localStorage.removeItem('contentrecommendation-listpage-filters');
      localStorage.removeItem('content-recommendations-urls');
      document.cookie = 'user_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'qttr_tools_auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      Object.keys(localStorage).forEach((key) => key.includes('reprocessModalSkipped') && localStorage.removeItem(key));
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(`contentrecommendation-listpage-filters`);
    }

    if (redirect && !isServerSide()) {
      Router.push(`/api/auth/logout?returnTo=${encodeURIComponent(callbackUrl)}`);
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        error: error.message,
        status: error.response?.status || 500,
        ok: false,
        url: callbackUrl,
      };
    }
  } finally {
    // clear out local storage state here as well
    // it's important we DO NOT throw an exception here
    // otherwise the user experience will be awful
  }

  // default error
  return {
    error: `Unknown error occurred`,
    status: undefined,
    ok: false,
    url: callbackUrl,
  };
};

/**
 *
 * @param session optional, if session is already fetched
 * @returns
 */
export const isTestDriveUser = async (session?: QwaSession) => {
  let qwaSession = session;

  if (!qwaSession) {
    qwaSession = session;
  }

  if (!qwaSession) {
    return undefined;
  }

  const qwaUser = qwaSession.user;
  if (!qwaUser) {
    return undefined;
  }

  const isOnlyAuthorizedForSubs =
    qwaUser.groups && Array.isArray(qwaUser.groups) && qwaUser.groups.length === 1 && qwaUser.groups[0] === SUBS_SCHEMA;
  const subscriberUserInfo = getSubscriberUserInfo();
  const testDriveDomain = subscriberUserInfo?.domain;

  return Boolean(isOnlyAuthorizedForSubs || testDriveDomain !== undefined);
};

export const isExpressCustomer = async (session, customerInfo) => {
  const custInfoRes = customerInfo;
  const userInfo = custInfoRes.data;
  if (!userInfo) {
    return false;
  }
  const schemaName = await getSchema(session);
  const customerDetails = userInfo.find((i) => i.cust_code === schemaName);
  const isExpressCustomer = customerDetails?.additional_user_info.isExpress;
  return isExpressCustomer === undefined ? false : isExpressCustomer;
};

/**
 *
 * @param session optional, if session is already fetched
 * @returns
 */
export const isSubscriberUser = async (session?: QwaSession) => {
  if (isServerSide()) {
    return false;
  }

  let qwaSession = session;
  if (!qwaSession) {
    qwaSession = session;
  }

  if (!qwaSession) {
    return false;
  }

  const userInfo = qwaSession.user;
  if (!userInfo) {
    return false;
  }

  const isSubscriberUser = qwaSession.selected_schema === SUBS_SCHEMA;
  return isSubscriberUser;
};

export const isInternalUser = async (session) => {
  if (!session) {
    return false;
  }

  const qwaSession = session as QwaSession;
  if (!qwaSession) {
    return false;
  }

  const qwaUser = qwaSession.user as QwaUser;
  if (!qwaUser) {
    return false;
  }

  const notTestDriveUser = !(await isTestDriveUser());
  return notTestDriveUser && qwaUser && qwaUser.email && qwaUser.email.endsWith('@quattr.com');
};

export interface SubscriberUserInfo {
  full_name: string;
  email: string;
  domain: string;
  id: string;
  /**
   * Issued At Time, milliseconds since epoch
   */
  iat: number;
  /**
   * Expiration timestamp, milliseconds since epoch
   */
  exp: number;
}

/**
 * Returns the current domain from the JWT/Session for a test drive user
 * @returns `undefined` if no domain is set or user is not a test drive user
 */
export const getTestDriveDomain = async () => {
  const subscriberUserInfo = getSubscriberUserInfo();
  if (!subscriberUserInfo) {
    return undefined;
  }

  return subscriberUserInfo.domain;
};

export interface FetchUserAuthTokenResult {
  user_auth_token?: string;
}

// not in use currently
export const lookerSignIn = async () => {
  try {
    const transport = Axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': true,
        'Sec-Fetch-Site': 'cross-site',
        'Access-Control-Allow-Methods': 'POST, GET, OPTIONS',
        'Access-Control-Allow-Headers': 'X-PINGOTHER, Content-Type',
      },
    });

    if (!process.env.LOOKER_SIGNIN_URL) {
      return undefined;
    }
    const resp = await transport.get(process.env.LOOKER_SIGNIN_URL);

    if (resp) {
      console.log('resp', resp);
    } else {
      console.error('Something went wrong');
    }
    return undefined;
  } catch (error) {
    console.log(error);
  }
};

export const getSchema = async (session) => {
  if (!session || !session.user) {
    return undefined;
  }
  const qwaSession = session;

  const userSelectedSchema = isServerSide() ? undefined : qwaSession.selected_schema;
  return userSelectedSchema;
};
